/**
 * PTEL Visor - Glosario de Términos Técnicos
 * Definiciones accesibles para usuarios no técnicos
 *
 * V03 - Medida: U02 (Glosario de términos técnicos)
 * Audiencia: Personal protección civil, técnicos ayuntamiento, operadores 112
 *
 * @see docs/INVESTIGACION_MULTIPERSPECTIVA_PTEL_20251210_0710.md
 */

// =============================================================================
// TIPOS
// =============================================================================

/** Entrada del glosario con término, etiqueta legible, descripción y ejemplo opcional */
export interface GlossaryEntry {
  /** Identificador técnico del término (ej: 'utm', 'epsg') */
  term: string;
  /** Nombre legible para mostrar (ej: 'Coordenadas UTM') */
  label: string;
  /** Descripción clara para no técnicos */
  description: string;
  /** Ejemplo práctico opcional */
  example?: string;
  /** Términos relacionados */
  related?: string[];
  /** Categoría para agrupación */
  category: GlossaryCategory;
}

/** Categorías de términos para organización */
export type GlossaryCategory =
  | 'coordenadas'
  | 'validacion'
  | 'mapa'
  | 'infraestructura'
  | 'emergencias';

// =============================================================================
// GLOSARIO
// =============================================================================

/**
 * Glosario completo de términos técnicos PTEL
 * Ordenado alfabéticamente por term
 */
export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    term: 'cdau',
    label: 'CDAU',
    description:
      'Callejero Digital de Andalucía Unificado. Base de datos oficial con todas las calles, números y direcciones de Andalucía.',
    example: 'Calle Mayor 15, Córdoba → coordenadas exactas del portal',
    related: ['geocodificacion', 'cartociudad'],
    category: 'coordenadas',
  },
  {
    term: 'cartociudad',
    label: 'CartoCiudad',
    description:
      'Servicio del Instituto Geográfico Nacional que convierte direcciones postales en coordenadas geográficas.',
    example: 'Plaza España 1, Sevilla → 37.3772, -5.9869',
    related: ['geocodificacion', 'cdau'],
    category: 'coordenadas',
  },
  {
    term: 'confianza',
    label: 'Nivel de confianza',
    description:
      'Indica cuánta seguridad tiene el sistema en que una coordenada es correcta. Verde = alta, amarillo = media, rojo = baja.',
    example: 'Confianza alta: coordenada verificada en varias fuentes',
    related: ['validacion', 'precision'],
    category: 'validacion',
  },
  {
    term: 'dera',
    label: 'DERA',
    description:
      'Datos Espaciales de Referencia de Andalucía. Conjunto de datos geográficos oficiales de la Junta de Andalucía.',
    example: 'Capa de centros de salud, colegios, parques de bomberos...',
    related: ['wfs', 'ieca'],
    category: 'infraestructura',
  },
  {
    term: 'epsg',
    label: 'Código EPSG',
    description:
      'Número que identifica un sistema de coordenadas específico. En Andalucía usamos principalmente EPSG:25830.',
    example: 'EPSG:25830 = Sistema UTM zona 30, usado en casi toda Andalucía',
    related: ['utm', 'huso'],
    category: 'coordenadas',
  },
  {
    term: 'geocodificacion',
    label: 'Geocodificación',
    description:
      'Proceso de convertir una dirección postal (calle, número, municipio) en coordenadas geográficas.',
    example: 'Av. Constitución 21, Sevilla → X: 234567, Y: 4142890',
    related: ['cartociudad', 'cdau'],
    category: 'coordenadas',
  },
  {
    term: 'huso',
    label: 'Huso UTM',
    description:
      'División vertical del mapa en franjas de 6 grados. Andalucía está entre los husos 29 (Huelva oeste) y 30 (resto).',
    example: 'Huelva capital está en huso 29, Almería en huso 30',
    related: ['utm', 'epsg'],
    category: 'coordenadas',
  },
  {
    term: 'ign',
    label: 'IGN',
    description:
      'Instituto Geográfico Nacional. Organismo estatal que proporciona mapas base, ortofotografías (PNOA) y servicios de geocodificación para toda España.',
    example: 'Mapa base IGN, ortofoto PNOA 2023, CartoCiudad',
    related: ['tiles', 'wms', 'cartociudad'],
    category: 'mapa',
  },
  {
    term: 'ieca',
    label: 'IECA',
    description:
      'Instituto de Estadística y Cartografía de Andalucía. Organismo que gestiona los datos geográficos oficiales.',
    related: ['dera', 'wfs'],
    category: 'infraestructura',
  },
  {
    term: 'infraestructura',
    label: 'Infraestructura crítica',
    description:
      'Instalación esencial para la comunidad: hospitales, colegios, bomberos, depósitos de agua, etc.',
    example: 'Hospital, colegio, parque de bomberos, depósito de agua',
    related: ['ptel', 'dera'],
    category: 'infraestructura',
  },
  {
    term: 'latitud',
    label: 'Latitud',
    description:
      'Distancia en grados desde el ecuador. En Andalucía va de 36° a 38.7° Norte aproximadamente.',
    example: 'Sevilla: 37.38° N (más al norte que Cádiz: 36.53° N)',
    related: ['longitud', 'wgs84'],
    category: 'coordenadas',
  },
  {
    term: 'longitud',
    label: 'Longitud',
    description:
      'Distancia en grados desde el meridiano de Greenwich. En Andalucía va de -7.5° a -1.6° Oeste.',
    example: 'Huelva: -6.95° O (más al oeste que Almería: -2.46° O)',
    related: ['latitud', 'wgs84'],
    category: 'coordenadas',
  },
  {
    term: 'normalizacion',
    label: 'Normalización',
    description:
      'Proceso de convertir coordenadas de distintos formatos al formato estándar UTM que usa el sistema.',
    example: '37°23\'15" N → 4140234 (coordenada Y en metros)',
    related: ['utm', 'validacion'],
    category: 'coordenadas',
  },
  {
    term: 'precision',
    label: 'Precisión',
    description:
      'Margen de error de una coordenada. Para emergencias necesitamos menos de 25 metros de error.',
    example: 'Precisión de 10m = el punto real está a menos de 10m del indicado',
    related: ['confianza', 'validacion'],
    category: 'validacion',
  },
  {
    term: 'ptel',
    label: 'PTEL',
    description:
      'Plan Territorial de Emergencias Local. Documento obligatorio para cada municipio con recursos y protocolos de emergencia.',
    example: 'El PTEL de Colomera incluye 103 infraestructuras críticas',
    related: ['infraestructura'],
    category: 'emergencias',
  },
  {
    term: 'tiles',
    label: 'Teselas del mapa',
    description:
      'Pequeñas imágenes cuadradas que forman el mapa. Se cargan según la zona visible para ahorrar datos.',
    example: 'Al hacer zoom, se cargan teselas más detalladas de esa zona',
    related: ['wms', 'ign'],
    category: 'mapa',
  },
  {
    term: 'utm',
    label: 'Coordenadas UTM',
    description:
      'Sistema de coordenadas en metros, más preciso que latitud/longitud para distancias cortas. Formato: X (este-oeste), Y (norte-sur).',
    example: 'X: 234567, Y: 4142890 (un punto en Sevilla)',
    related: ['epsg', 'huso'],
    category: 'coordenadas',
  },
  {
    term: 'validacion',
    label: 'Validación',
    description:
      'Comprobación automática de que una coordenada está dentro de Andalucía y tiene el formato correcto.',
    example: 'X: 900000 → Error: fuera de rango válido para Andalucía',
    related: ['confianza', 'precision'],
    category: 'validacion',
  },
  {
    term: 'wfs',
    label: 'Servicio WFS',
    description:
      'Web Feature Service. Servicio web que permite descargar datos geográficos (puntos, líneas, polígonos).',
    example: 'WFS del IECA con todos los centros de salud de Andalucía',
    related: ['wms', 'dera'],
    category: 'mapa',
  },
  {
    term: 'wgs84',
    label: 'WGS84',
    description:
      'Sistema de coordenadas global usado por GPS y Google Maps. Usa latitud y longitud en grados.',
    example: '37.3891, -5.9845 (Sevilla en WGS84)',
    related: ['latitud', 'longitud', 'utm'],
    category: 'coordenadas',
  },
  {
    term: 'wms',
    label: 'Servicio WMS',
    description:
      'Web Map Service. Servicio web que genera imágenes de mapas (ortofoto, callejero, topográfico).',
    example: 'WMS del IGN con la ortofoto PNOA de toda España',
    related: ['wfs', 'tiles'],
    category: 'mapa',
  },

  // ─── Cotejo con el catálogo oficial (P3) — lenguaje llano para técnico municipal ──
  {
    term: 'ausente',
    label: 'Ausente',
    description:
      'Infraestructura que figura en el catálogo oficial pero que tu plan todavía no recoge. Conviene revisarla y, si procede, añadirla al PTEL.',
    example: 'Un colegio del catálogo de la Junta que no aparece en el plan del municipio.',
    related: ['cotejo', 'completitud'],
    category: 'infraestructura',
  },
  {
    term: 'candidato',
    label: 'Candidato',
    description:
      'Coordenada alternativa que el sistema propone para una infraestructura cuando la ubicación original puede no ser correcta. Tú decides si la aceptas.',
    example: 'Para un centro de salud sin coordenada fiable, se sugiere un punto del callejero oficial.',
    related: ['geocodificacion'],
    category: 'infraestructura',
  },
  {
    term: 'completitud',
    // TERMCOT-1 DOP-6 a: la clave sigue siendo `completitud`; cambia lo que se lee.
    label: 'Cobertura del catálogo',
    description:
      'Porcentaje de las infraestructuras del catálogo oficial de tu municipio que tu plan ya recoge. Cada ausente lleva su criticidad, que indica cuánto pesa en la normativa y con qué urgencia revisarlo.',
    example: 'Si el catálogo registra 40 y el plan recoge 36, la cobertura del catálogo es del 90 %.',
    related: ['cotejo', 'ausente', 'criticidad'],
    category: 'emergencias',
  },
  {
    term: 'cotejo',
    label: 'Cotejo con el catálogo oficial',
    description:
      'Comparación de las infraestructuras de tu plan con el catálogo oficial para ver cuáles faltan, cuáles sobran y cuáles coinciden.',
    example: 'Al cotejar, el municipio detecta 2 infraestructuras ausentes y 1 sobrante.',
    related: ['completitud', 'ausente', 'sobrante'],
    category: 'emergencias',
  },
  {
    term: 'criticidad',
    label: 'Criticidad',
    description:
      'Importancia de una infraestructura ante una emergencia según la normativa. A mayor criticidad, más prioridad para tenerla bien ubicada en el plan.',
    example: 'Un hospital tiene criticidad alta; una caseta de aperos, baja.',
    related: ['infraestructura'],
    category: 'emergencias',
  },
  {
    term: 'sobrante',
    label: 'Sobrante',
    description:
      'Infraestructura que tu plan incluye pero que no figura en el catálogo oficial. Puede ser correcta (algo propio del municipio) o un error que conviene revisar.',
    example: 'Un punto de encuentro local que no aparece en el catálogo de la Junta.',
    related: ['cotejo'],
    category: 'infraestructura',
  },
] as const;

// =============================================================================
// FUNCIONES DE ACCESO
// =============================================================================

/**
 * Obtiene una entrada del glosario por su término técnico
 * @param term - Identificador del término (case-insensitive)
 * @returns La entrada del glosario o undefined si no existe
 */
export function getGlossaryEntry(term: string): GlossaryEntry | undefined {
  const normalizedTerm = term.toLowerCase().trim();
  return GLOSSARY.find((entry) => entry.term === normalizedTerm);
}

/**
 * Obtiene todas las entradas de una categoría
 * @param category - Categoría a filtrar
 * @returns Array de entradas de esa categoría
 */
export function getGlossaryByCategory(category: GlossaryCategory): GlossaryEntry[] {
  return GLOSSARY.filter((entry) => entry.category === category);
}

/**
 * Busca entradas que contengan el texto en label o description
 * @param query - Texto a buscar (case-insensitive)
 * @returns Array de entradas coincidentes
 */
export function searchGlossary(query: string): GlossaryEntry[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return [];

  return GLOSSARY.filter(
    (entry) =>
      entry.label.toLowerCase().includes(normalizedQuery) ||
      entry.description.toLowerCase().includes(normalizedQuery) ||
      entry.term.includes(normalizedQuery)
  );
}

/**
 * Obtiene los términos relacionados de una entrada
 * @param term - Identificador del término
 * @returns Array de entradas relacionadas
 */
export function getRelatedTerms(term: string): GlossaryEntry[] {
  const entry = getGlossaryEntry(term);
  if (!entry?.related) return [];

  return entry.related
    .map((relatedTerm) => getGlossaryEntry(relatedTerm))
    .filter((e): e is GlossaryEntry => e !== undefined);
}

/**
 * Lista todas las categorías disponibles con conteo
 * @returns Mapa de categoría → número de términos
 */
export function getCategoryCounts(): Map<GlossaryCategory, number> {
  const counts = new Map<GlossaryCategory, number>();

  for (const entry of GLOSSARY) {
    counts.set(entry.category, (counts.get(entry.category) || 0) + 1);
  }

  return counts;
}

/**
 * Número total de términos en el glosario
 */
export const GLOSSARY_SIZE = GLOSSARY.length;
